/**
 * A plane's marks, live in a tldraw store. The plane's `records` on the
 * file's own Automerge doc are the source; the store is a view of them on one
 * page whose id is the plane's. One reconcile brings the store to the doc, on
 * load and on every doc change (our own edits reconcile to nothing); edits
 * made in the store write back. Stored marks come up from the tldraw schema
 * the plane records they were written with, and a plane opened under a newer
 * schema is rewritten under it, so the two never disagree.
 */
import type { DocHandle } from "@automerge/automerge-repo";
import {
  keepMarkThreads,
  type AnnotatedDoc,
  type Plane,
  type PlaneRecord,
} from "playground/annotations/annotations";
import {
  PageRecordType,
  ZERO_INDEX_KEY,
  isEqual,
  type SerializedSchema,
  type TLRecord,
  type TLStore,
} from "tldraw";
import { invariant } from "ts-invariant";

/** Only shapes and bindings are marks; the page and the rest are the view's. */
const isMark = (record: { typeName: string }) =>
  record.typeName === "shape" || record.typeName === "binding";

/** Plane units per sheet pixel of lettering: 13-unit letters, about 11 px
 *  at the zoom a plane is made at (1000 units across ~850 px). */
const PLANE_ANNOTATION_SCALE = 1;

export const planePageId = (planeId: string) =>
  PageRecordType.createId(planeId);

function planeOf(doc: AnnotatedDoc | undefined, planeId: string) {
  const plane = doc?.[`plane:${planeId}`];
  invariant(plane, `Unknown plane: ${planeId}`);
  return plane;
}

/** A stored mark, brought up from the schema it was written with and then
 *  checked against the store's schema on its way in. */
function markOf(store: TLStore, record: PlaneRecord, schema: Plane["schema"]) {
  const { typeName } = record;
  invariant(
    typeName === "shape" || typeName === "binding",
    `Not a mark: ${record.id}`,
  );
  // Migration reads stored data as the version it was written at; the
  // validation after it is what makes it a record of this schema.
  const migrated = store.schema.migratePersistedRecord(
    record as unknown as TLRecord,
    schema as unknown as SerializedSchema,
  );
  invariant(
    migrated.type === "success",
    `Can't bring ${record.id} up to date: ${migrated.type === "error" ? migrated.reason : ""}`,
  );
  return store.schema.types[typeName].validate(migrated.value);
}

function reconcile(store: TLStore, plane: Plane) {
  const marks = Object.values(plane.records).map((r) =>
    markOf(store, r, plane.schema),
  );
  const ids = new Set<string>(marks.map((r) => r.id));
  const stale = store
    .allRecords()
    .filter((r) => isMark(r) && !ids.has(r.id))
    .map((r) => r.id);
  const changed = marks.filter((r) => !isEqual(store.get(r.id), r));
  store.mergeRemoteChanges(() => {
    store.remove(stale);
    store.put(changed);
  });
}

/** Make the plane's page the store's only page, holding its marks. */
export function loadPlane(store: TLStore, plane: Plane) {
  const pageId = planePageId(plane.id);
  const otherPages = store
    .allRecords()
    .filter((r) => r.typeName === "page" && r.id !== pageId)
    .map((r) => r.id);
  store.mergeRemoteChanges(() => {
    store.put([
      PageRecordType.create({
        id: pageId,
        name: "Plane",
        index: ZERO_INDEX_KEY,
        // Marks are fixed to the plane, callouts too: lettering scales with
        // the plane instead of holding its size on screen.
        meta: { annotationScale: PLANE_ANNOTATION_SCALE },
      }),
    ]);
    store.remove(otherPages);
  });
  reconcile(store, plane);
}

function holdMarkThreads(handle: DocHandle<AnnotatedDoc>, planeId: string) {
  const heads = [...(handle.heads() ?? [])];
  handle.change((doc) => keepMarkThreads(doc, planeId, heads));
}

/** Load the plane into the store and keep the two in step until disposed. */
export function syncPlaneStore(
  store: TLStore,
  handle: DocHandle<AnnotatedDoc>,
  planeId: string,
): () => void {
  loadPlane(store, planeOf(handle.doc(), planeId));
  const schema = store.schema.serialize();
  if (!isEqual(planeOf(handle.doc(), planeId).schema, schema)) {
    handle.change((doc) => {
      const plane = planeOf(doc, planeId);
      plane.schema = { ...schema };
      plane.records = Object.fromEntries(
        store
          .allRecords()
          .filter(isMark)
          .map((r) => [r.id, { ...structuredClone(r) }]),
      );
    });
  }

  // Every mark has its thread: marks drawn before that rule get theirs now.
  holdMarkThreads(handle, planeId);

  // Local edits tldraw hasn't delivered yet are written first, so reconciling
  // to the doc never rolls them back.
  const onDocChange = () => {
    store._flushHistory();
    reconcile(store, planeOf(handle.doc(), planeId));
  };
  handle.on("change", onDocChange);
  const stopListening = store.listen(
    ({ changes }) => {
      const put = [
        ...Object.values(changes.added),
        ...Object.values(changes.updated).map(([, to]) => to),
      ].filter(isMark);
      const removed = Object.values(changes.removed).filter(isMark);
      if (!put.length && !removed.length) return;
      const heads = [...(handle.heads() ?? [])];
      // A mark and its thread land in one change, so no peer sees one alone.
      handle.change((doc) => {
        const { records } = planeOf(doc, planeId);
        for (const record of put) {
          records[record.id] = { ...structuredClone(record) };
        }
        for (const record of removed) delete records[record.id];
        keepMarkThreads(doc, planeId, heads);
      });
    },
    { source: "user", scope: "document" },
  );
  return () => {
    // tldraw holds listener notifications until the next frame; deliver the
    // last edits while the write-back listener still hears them.
    store._flushHistory();
    stopListening();
    handle.off("change", onDocChange);
  };
}
